import React from "react";
import './Clients.css';
import authService from "./AuthService";
import config, { getApiUrl } from "./config";
import toast from "react-hot-toast";
import {Endpoint} from "./Endpoints";
import ClientForm from "./ClientForm";
import {EnpointAccess} from "./ClientEndpointAccess";
import ManageableAccess from "./ManageableAccess";

type ClientsProperties = {}

export type Client = {
    id: string,
    name: string,
    editor: string,
    metadata: string,
    PlatformAccess: Array<string>
    EndpointsAccess: Array<EnpointAccess>
}

type ClientsState = {
    clients: Array<Client>
    platformAccessEdited: boolean
    endpointAccessEdited: boolean
    editedClient: Client
    editedEndpoint: EnpointAccess
    accessLoaded: Record<string, boolean>
    loadingClientAccessId: string | null
    endpointsCache: Endpoint[]
    selectedClientId: string | null
    newEndpointId: string
};

type PlatformAccess = {
    scopes: Array<string>
};

const platformScopes: string[] = [
    "appliance-driver-image-management",
    "hal-image-management",
    "local-application-image-upload",
    "authorization-management",
    "controller-platform",
    "read-components",
    "endpoint-creation",
    "core-image-management",
];

const endpointScopes: string[] = [
    "endpoint-description-document-management",
    "hal-description-document-management",
    "local-application-document-management",
    "live-data",
    "history-data",
    "authorization-management",
    "core-update-management",
    "endpoint-management",
    "events",
    "setpoints-control",
    "status",
    "read-endpoint-description",
    "external-data-management"
];

class Clients extends React.Component<ClientsProperties, ClientsState> {
    private updateIntervalId: number | undefined;

    constructor(props: ClientsProperties) {
        super(props);
        this.state = {
            clients: new Array<Client>(),
            platformAccessEdited: false,
            endpointAccessEdited: false,
            editedClient: {
                id: "",
                name: "",
                editor: "",
                metadata: "",
                PlatformAccess: [],
                EndpointsAccess: []
            },
            editedEndpoint: {
                EndpointId: "",
                FriendlyName: "",
                Access: []
            },
            accessLoaded: {},
            loadingClientAccessId: null,
            endpointsCache: [],
            selectedClientId: null,
            newEndpointId: ""
        }
    }

    componentDidMount() {
        this.fetchClients();
        this.updateIntervalId = window.setInterval(() => this.fetchClients(), 5000);
    }

    componentWillUnmount() {
        clearInterval(this.updateIntervalId);
    }

    render() {
        const selectedClient = this.state.selectedClientId ? this.getClientFromState(this.state.selectedClientId) : null;
        return (
            <div className="Clients">
                <h2>Clients</h2>
                {this.renderClients()}
                <h2>Add Client</h2>
                <ClientForm/>

                {selectedClient && this.renderClientModal(selectedClient)}
            </div>
        );
    }

    private renderClients() {
        return (
            <div className="Clients-list">
                {Array.isArray(this.state.clients) && this.state.clients.length > 0 ? (
                    <div className="Endpoints-list">
                        {this.state.clients.map((client) => (
                            <div
                                key={client.id}
                                className="Endpoints-endpoint"
                                onClick={() => this.handleClientClick(client)}
                            >
                                <div className="Clients-name">{client.name}</div>
                                <div className="Clients-id">{client.id}</div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div>No clients available</div>
                )}
                {this.state.platformAccessEdited &&  
                    <ManageableAccess 
                        previouslySelectedScopes={this.state.editedClient.PlatformAccess}
                        scopeList={platformScopes}
                        onSave={this.onAccessManagementSaved}
                        onClose={this.onAccessManagementClosed}
                    />
                }
                {this.state.endpointAccessEdited &&  
                    <ManageableAccess 
                        previouslySelectedScopes={this.state.editedEndpoint.Access}
                        scopeList={endpointScopes}
                        onSave={this.onAccessManagementSaved}
                        onClose={this.onAccessManagementClosed}
                    />
                }
                
            </div>
        );
    }

    onAccessManagementSaved = (scopesToAdd: Array<string>, scopesToRemove: Array<string>) => {
        if (this.state.platformAccessEdited) {
            this.onPlatformAccessSaved(this.state.editedClient.id, scopesToAdd, scopesToRemove);
        } else if (this.state.endpointAccessEdited) {
            this.onEndpointAccessSaved(this.state.editedClient.id, this.state.editedEndpoint.EndpointId, scopesToAdd, scopesToRemove);
        }
    }

    onAccessManagementClosed = () => {
        this.setState({endpointAccessEdited: false, platformAccessEdited: false});
    }

    onManageEndpointAccess = (client: Client, endpoint: EnpointAccess) => {
        // Require access to be loaded before editing
        if (!this.state.accessLoaded[client.id]) {
            this.onLoadAccess(client.id).then(() => {
                this.setState({endpointAccessEdited: true, editedClient: this.getClientFromState(client.id), editedEndpoint: endpoint});
            });
            return;
        }

        this.setState({endpointAccessEdited: true
            , editedClient: this.getClientFromState(client.id)
            , editedEndpoint: endpoint
        });
    }

    onEndpointAccessSaved = (clientId: string, endpointId: string, scopesToAdd: Array<string>, scopesToRemove: Array<string>) => {
        this.addEndpointAccess(clientId, endpointId, scopesToAdd).then(() => {
            this.removeEndpointAccess(clientId, endpointId, scopesToRemove).then(() => {
                this.fetchClients();
            });
        });
    }

    onManagePlatformAccess = (client: Client) => {
        if (!this.state.accessLoaded[client.id]) {
            this.onLoadAccess(client.id).then(() => {
                this.setState({platformAccessEdited: true, editedClient: this.getClientFromState(client.id)});
            });
            return;
        }

        this.setState({platformAccessEdited: true
            , editedClient: this.getClientFromState(client.id)
        });
    }

    onPlatformAccessSaved = (clientId: string, scopesToAdd: Array<string>, scopesToRemove: Array<string>) => {
        this.addPlatformAccess(clientId, scopesToAdd).then(() => {
            this.removePlatformAccess(clientId, scopesToRemove).then(() => {
                this.fetchClients();
            });
        });
    }

    private fetchClients() {
        fetch(getApiUrl() + "/v1/client/", {
            headers: [
                ["Accept", "application/json"],
                ["Authorization", authService.authHeader()]
            ]
        })
            .then(response => response.json() as Promise<Client[]>)
            .then((response) => {
                this.setState((prevState) => {
                    const accessLoaded = { ...prevState.accessLoaded };
                    const clients = response.map((client) => {
                        const existing = prevState.clients.find((c) => c.id === client.id);
                        return {
                            ...client,
                            PlatformAccess: existing?.PlatformAccess ?? [],
                            EndpointsAccess: existing?.EndpointsAccess ?? []
                        } as Client;
                    });
                    return { clients, accessLoaded };
                });
            })
            .catch(() => {
                toast.error("Failed to fetch clients");
            });
    }

    private onLoadAccess = (clientId: string) => {
        this.setState({ loadingClientAccessId: clientId });
        const endpointsPromise = this.state.endpointsCache.length > 0
            ? Promise.resolve(this.state.endpointsCache)
            : this.fetchEndpoints().then((eps) => {
                this.setState({ endpointsCache: eps });
                return eps;
            });

        return Promise.all([
            this.fetchPlatformAccess(clientId),
            endpointsPromise.then((eps) => this.fetchEndpointsAccess(clientId, eps))
        ])
            .then(([platformAccess, endpointsAccess]) => {
                this.setState((prev) => ({
                    clients: prev.clients.map((c) => c.id === clientId ? { ...c, PlatformAccess: platformAccess, EndpointsAccess: endpointsAccess } : c),
                    accessLoaded: { ...prev.accessLoaded, [clientId]: true },
                    loadingClientAccessId: null
                }));
            })
            .catch((error) => {
                toast.error("Failed to load client access: " + error.message);
                this.setState({ loadingClientAccessId: null });
            });
    }

    
    private fetchPlatformAccess(clientId: string): Promise<Array<string>> {
        return fetch(getApiUrl() + "/v1/client/" + clientId + "/access", {
            headers: [
                ["Accept", "application/json"],
                ["Authorization", authService.authHeader()]
            ]
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json() as Promise<PlatformAccess>;
            })
            .then((response) => {
                return response.scopes;
            })
            .catch((error) => {
                toast.error("Failed to fetch platform access: " + error.message);
                return [];
            });
    }

    private fetchEndpointsAccess(clientId: string, endpoints?: Endpoint[]): Promise<Array<EnpointAccess>> {
        const endpointsPromise = endpoints ? Promise.resolve(endpoints) : this.fetchEndpoints();

        return endpointsPromise.then((resolvedEndpoints) => {
            const endpointAccessPromises = resolvedEndpoints.map(endpoint => {
                return this.fetchEndpointAccess(clientId, endpoint.endpointId).then((access) => {
                    if (access.length === 0) {
                        return null; // Return null for endpoints with no access
                    }
                    return {
                        EndpointId: endpoint.endpointId,
                        FriendlyName: endpoint.friendlyName,
                        Access: access
                    };
                });
            });

            return Promise.all(endpointAccessPromises).then(results => {
                // Filter out null values
                return results.filter(result => result !== null) as Array<EnpointAccess>;
            });
        });
    }

    private getClientFromState(clientId: string): Client {
        return this.state.clients.find((c) => c.id === clientId) as Client;
    }

    private onAddEndpointAccess = (client: Client) => {
        const endpointId = this.state.newEndpointId.trim();
        if (!endpointId) {
            return;
        }
        const endpoint: EnpointAccess = {
            EndpointId: endpointId,
            FriendlyName: "",
            Access: []
        };
        this.setState({ editedClient: client, editedEndpoint: endpoint, endpointAccessEdited: true, newEndpointId: "" });
    };

    private handleClientClick(client: Client) {
        this.setState({ selectedClientId: client.id });
        if (!this.state.accessLoaded[client.id]) {
            this.onLoadAccess(client.id);
        }
    }

    private renderClientModal(client: Client) {
        const loading = this.state.loadingClientAccessId === client.id;
        const accessLoaded = !!this.state.accessLoaded[client.id];

        return (
            <div className="Client-modal-overlay">
                <div className="Client-modal">
                    <div className="Client-modal-header">
                        <h3>{client.name}</h3>
                        <span className="Client-id">{client.id}</span>
                    </div>
                    {loading && <div className="loading-hint">Loading access…</div>}
                    {accessLoaded && !loading && (
                        <div className="Client-modal-body">
                            <div className="Client-section">
                                <div className="section-title">Platform access</div>
                                {client.PlatformAccess.length > 0 ? (
                                    <div className="scope-list">
                                        {client.PlatformAccess.map((scope, idx) => (
                                            <div key={idx} className="scope">{scope}</div>
                                        ))}
                                    </div>
                                ) : (
                                    <div>No platform access</div>
                                )}
                                <button className="manage-platform-access-btn" onClick={() => this.onManagePlatformAccess(client)}>Manage platform access</button>
                            </div>

                            <div className="Client-section">
                                <div className="section-title">Endpoint access</div>
                                {client.EndpointsAccess.length > 0 ? (
                                    client.EndpointsAccess.map((ep, idx) => (
                                        <div key={idx} className="Endpoint-access">
                                            <div className="Endpoint-access-title">{ep.FriendlyName || ep.EndpointId}</div>
                                            <div className="Client-id">{ep.EndpointId}</div>
                                            <div className="scope-list">
                                                {ep.Access.map((s, i) => (<span key={i} className="scope">{s}</span>))}
                                            </div>
                                            <button onClick={() => this.onManageEndpointAccess(client, ep)}>Manage endpoint access</button>
                                        </div>
                                    ))
                                ) : (
                                    <div>No endpoint access</div>
                                )}
                                <div className="add-endpoint-access">
                                    <input
                                        type="text"
                                        placeholder="Endpoint UUID"
                                        value={this.state.newEndpointId}
                                        onChange={(e) => this.setState({ newEndpointId: e.target.value })}
                                    />
                                    <button
                                        onClick={() => this.onAddEndpointAccess(client)}
                                        disabled={!this.state.newEndpointId}
                                    >
                                        Add endpoint access
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {!accessLoaded && !loading && (
                        <div className="loading-hint">Click Load access to fetch details.</div>
                    )}
                    <div className="Client-modal-actions">
                        <button onClick={() => this.onLoadAccess(client.id)} disabled={loading}>Reload access</button>
                        <button onClick={() => this.setState({ selectedClientId: null })}>Close</button>
                    </div>
                </div>
            </div>
        );
    }

    private fetchEndpoints(): Promise<Endpoint[]> {
        return fetch(getApiUrl() + "/v1/endpoint/", {
            headers: [
                ["Accept", "application/json"],
                ["Authorization", authService.authHeader()]
            ]
        })
            .then(response => response.json() as Promise<Endpoint[]>)
            .then((response) => {
                return response;
            })
            .catch((error) => {
                console.error(error);
                return [];
            });
    }

    private fetchEndpointAccess(clientId: string, endpointId: string): Promise<Array<string>> {
        return fetch(getApiUrl() + "/v1/client/" + clientId + "/endpoint/" + endpointId + "/access", {
            headers: [
                ["Accept", "application/json"],
                ["Authorization", authService.authHeader()]
            ]
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json().then((data) => {
                    return data.scopes;
                });
            })
            .catch((error) => {
                toast.error("Failed to fetch endpoint access: " + error.message);
                return [];
            });
    }

    private addEndpointAccess(clientId: string, endpointId: string, scopes: Array<string>) {
        const scopesQuery = scopes.map(scope => encodeURIComponent(scope)).join(',');
        if (scopesQuery.length === 0) {
            return Promise.resolve();
        }
        return fetch(`${getApiUrl()}/v1/client/${clientId}/endpoint/${endpointId}/access?scopes=${scopesQuery}`, {
            method: "PUT",
            headers: [
                ["Accept", "application/json"],
                ["Authorization", authService.authHeader()]
            ]
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error("Failed to add endpoint access");
                }
            })
            .catch((error) => {
                toast.error("Failed to add endpoint access: " + error.message);
            });
    }

    private removeEndpointAccess(clientId: string, endpointId: string, scopes: Array<string>) {
        const scopesQuery = scopes.map(scope => encodeURIComponent(scope)).join(',');
        if (scopesQuery.length === 0) {
            return Promise.resolve();
        }
        return fetch(`${getApiUrl()}/v1/client/${clientId}/endpoint/${endpointId}/access?scopes=${scopesQuery}`, {
            method: "DELETE",
            headers: [
                ["Accept", "application/json"],
                ["Authorization", authService.authHeader()]
            ]
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error("Failed to remove endpoint access");
                }
            })
            .catch((error) => {
                toast.error("Failed to remove endpoint access: " + error.message);
            });
    }

    private addPlatformAccess(clientId: string, scopes: Array<string>) {
        const scopesQuery = scopes.map(scope => encodeURIComponent(scope)).join(',');
        if (scopesQuery.length === 0) {
            return Promise.resolve();
        }
        return fetch(`${getApiUrl()}/v1/client/${clientId}/access?scopes=${scopesQuery}`, {
            method: "PUT",
            headers: [
                ["Accept", "application/json"],
                ["Authorization", authService.authHeader()]
            ]
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error("Failed to add platform access");
                }
            })
            .catch((error) => {
                toast.error("Failed to add platform access: " + error.message);
            });
    }

    private removePlatformAccess(clientId: string, scopes: Array<string>) {
        const scopesQuery = scopes.map(scope => encodeURIComponent(scope)).join(',');
        if (scopesQuery.length === 0) {
            return Promise.resolve();
        }
        return fetch(`${getApiUrl()}/v1/client/${clientId}/access?scopes=${scopesQuery}`, {
            method: "DELETE",
            headers: [
                ["Accept", "application/json"],
                ["Authorization", authService.authHeader()]
            ]
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error("Failed to remove platform access");
                }
            })
            .catch((error) => {
                toast.error("Failed to remove platform access: " + error.message);
            });
    }
}

export default Clients;
